import React from 'react';
import {
  ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Area, AreaChart,
} from 'recharts';
import { useTranslation } from 'react-i18next';

const AXIS = '#B9C4CF';
const GRID = 'rgba(255,255,255,0.08)';

export const ScoreRadar = ({ scores }) => {
  const { t } = useTranslation();
  if (!scores) return null;
  const data = [
    { k: t('charts.body'), v: scores.body_position },
    { k: t('charts.kite'), v: scores.kite_control },
    { k: t('charts.edge'), v: scores.board_edge },
    { k: t('charts.timing'), v: scores.timing },
    { k: t('charts.style'), v: scores.style },
  ];
  return (
    <ResponsiveContainer width="100%" height={260}>
      <RadarChart data={data} outerRadius="72%">
        <PolarGrid stroke={GRID} />
        <PolarAngleAxis dataKey="k" tick={{ fill: AXIS, fontSize: 12 }} />
        <PolarRadiusAxis domain={[0, 10]} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} />
        <Radar dataKey="v" stroke="#00B4D8" fill="#00B4D8" fillOpacity={0.35} />
      </RadarChart>
    </ResponsiveContainer>
  );
};

export const ProgressLine = ({ data, metric = 'overall', color = '#00B4D8' }) => {
  if (!data || data.length === 0) return null;
  const gid = `grad-${metric}`;
  return (
    <ResponsiveContainer width="100%" height={260}>
      <AreaChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.5} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="date" tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={{ stroke: GRID }} />
        <YAxis domain={[0, 10]} tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={false} />
        <Tooltip contentStyle={{ background: '#12161B', border: `1px solid ${color}59`, borderRadius: 12, color: '#fff' }} />
        <Area type="monotone" dataKey={metric} stroke={color} strokeWidth={2.5} fill={`url(#${gid})`} connectNulls dot={data.length < 12} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export const WindForecastChart = ({ data, unit = 'kn' }) => {
  const { t } = useTranslation();
  if (!data || data.length === 0) return null;
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="label" tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={{ stroke: GRID }} />
        <YAxis tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={false} unit={unit} />
        <Tooltip contentStyle={{ background: '#12161B', border: '1px solid rgba(72,202,228,0.35)', borderRadius: 12, color: '#fff' }} />
        <Bar dataKey="gust" name={t('charts.gust')} fill="rgba(72,202,228,0.25)" radius={[4, 4, 0, 0]} />
        <Bar dataKey="wind" name={t('charts.wind')} fill="#00B4D8" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export const HistoryChart = ({ data, bestMonths = [], unit = 'kn' }) => {
  const { t } = useTranslation();
  if (!data || data.length === 0) return null;
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="label" tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={{ stroke: GRID }} />
        <YAxis tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={false} unit={unit} />
        <Tooltip contentStyle={{ background: '#12161B', border: '1px solid rgba(72,202,228,0.35)', borderRadius: 12, color: '#fff' }}
          formatter={(v) => [`${v} ${unit}`, t('charts.avgWind')]} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
        <Bar dataKey="avg_wind" name={t('charts.avgWind')} radius={[4, 4, 0, 0]}>
          {data.map((d, i) => (
            <Cell key={i} fill={bestMonths.includes(d.month) ? '#FF6B35' : '#00B4D8'} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};
